import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { SITE } from "@/data/site";
import AppointmentDialog from "@/components/AppointmentDialog";

const NAV = [
  { to: "/", label: "Anasayfa" },
  { to: "/hizmetler", label: "Hizmetler" },
  { to: "/hakkimizda", label: "Hakkımızda" },
  { to: "/galeri", label: "Galeri" },
  { to: "/hediye-ceki", label: "Hediye Çeki" },
  { to: "/iletisim", label: "İletişim" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [apptOpen, setApptOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 32);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const isHome = pathname === "/";
  const transparent = isHome && !scrolled && !open;
  const dark = !transparent;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          dark
            ? "bg-bone/90 backdrop-blur-xl border-b border-pv shadow-[0_4px_30px_rgba(31,64,54,0.05)] py-3 text-ink"
            : "bg-transparent py-5 text-cream"
        }`}
      >
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 flex items-center justify-between gap-6">
          {/* Logo */}
          <Link to="/" data-testid="header-logo" className="group flex items-center shrink-0">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <img
                src={SITE.logoUrl}
                alt="Diamond Life Bursa — Premium Masaj Salonu Logo"
                className={`logo-tone block transition-all duration-500 ${dark ? "h-14 md:h-16" : "h-16 md:h-20"} w-auto select-none`}
                draggable={false}
              />
            </motion.div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-9 shrink-0">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.to.replace(/\//g, "") || "home"}`}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `relative text-[11px] tracking-[0.22em] uppercase font-medium transition-colors duration-300 ${
                    isActive
                      ? (dark ? "text-forest" : "text-bronze-soft")
                      : (dark ? "text-ink-soft hover:text-forest" : "text-cream/85 hover:text-cream")
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="relative">
                    {n.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline-pv"
                        className="absolute -bottom-2 left-0 right-0 h-[1px] bg-bronze"
                      />
                    )}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Right actions */}
          <div className="hidden md:flex items-center gap-2 lg:gap-3 shrink-0">
            <a
              data-testid="header-phone-btn"
              href={`tel:${SITE.phoneRaw}`}
              className={`hidden xl:flex items-center gap-2 text-xs tracking-[0.12em] uppercase whitespace-nowrap transition-colors ${dark ? "text-ink-soft hover:text-forest" : "text-cream/90 hover:text-cream"}`}
            >
              <Phone className="w-3.5 h-3.5 text-bronze-soft" />
              <span>{SITE.phoneDisplay}</span>
            </a>
            <a
              data-testid="header-whatsapp-btn"
              href={`https://wa.me/${SITE.whatsappRaw}`}
              target="_blank"
              rel="noreferrer"
              className={`pv-btn text-[10px] py-2.5 px-3 lg:px-4 ${dark ? "" : "pv-btn-light"}`}
            >
              WhatsApp
            </a>
            <button
              data-testid="header-appointment-btn"
              onClick={() => setApptOpen(true)}
              className="pv-btn pv-btn-bronze text-[10px] py-2.5 px-3 lg:px-4 whitespace-nowrap"
            >
              Randevu Al
            </button>
          </div>

          {/* Mobile burger */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-2 -mr-2 ${dark ? "text-ink" : "text-cream"}`}
            aria-label="Menüyü aç"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
              className="lg:hidden mt-3 mx-4 bg-bone border border-pv rounded-sm overflow-hidden shadow-xl"
            >
              <nav className="flex flex-col">
                {NAV.map((n, i) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    data-testid={`mobile-nav-${n.to.replace(/\//g, "") || "home"}`}
                    className="px-6 py-4 border-b border-pv-soft text-sm tracking-[0.18em] uppercase text-ink-soft"
                  >
                    <span className="text-bronze mr-3 text-[10px]">0{i + 1}</span>
                    {n.label}
                  </NavLink>
                ))}
                <div className="grid grid-cols-2 gap-2 p-4">
                  <a
                    data-testid="mobile-whatsapp-btn"
                    href={`https://wa.me/${SITE.whatsappRaw}`}
                    target="_blank"
                    rel="noreferrer"
                    className="pv-btn text-[10px] py-3"
                  >
                    WhatsApp
                  </a>
                  <button
                    data-testid="mobile-appointment-btn"
                    onClick={() => setApptOpen(true)}
                    className="pv-btn pv-btn-solid text-[10px] py-3"
                  >
                    Randevu
                  </button>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <AppointmentDialog open={apptOpen} onOpenChange={setApptOpen} />
    </>
  );
}
